import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { fmt, API } from "@/lib/api";
import { toast } from "sonner";
import { FileText, Package, TrendingUp, Receipt, Crown, ArrowRight, Upload, Download, Plus, FileSpreadsheet, ScanLine, BellRing, MessageCircle, Check } from "lucide-react";
import Sparkline from "@/components/Sparkline";

export default function Dashboard() {
    const [s, setS] = useState(null);
    const nav = useNavigate();
    const fileRef = useRef();
    const [uploading, setUploading] = useState(false);

    const load = () => api.get("/dashboard/summary").then((r) => setS(r.data));
    useEffect(() => { load(); }, []);

    if (!s) return <div className="text-slate-500">Loading…</div>;

    const cards = [
        { label: "Invoices (all time)", value: s.invoice_count, icon: FileText, tag: "mint" },
        { label: "This month", value: s.invoices_this_month, icon: Receipt, tag: "gold" },
        { label: "Items in Master", value: s.items_count, icon: Package, tag: "slate" },
        { label: "Tax collected", value: fmt(s.total_tax), icon: TrendingUp, tag: "mint" },
    ];

    const reorder = (c) => {
        const p = new URLSearchParams({ name: c.buyer.name || "", gstin: c.buyer.gstin || "" });
        nav(`/invoices/new?${p.toString()}`);
    };

    const upload = async (e) => {
        const f = e.target.files?.[0]; if (!f) return;
        setUploading(true);
        const fd = new FormData(); fd.append("file", f);
        try {
            const r = await api.post("/items/bulk-import", fd);
            toast.success(`Rates uploaded — ${r.data.inserted} added, ${r.data.updated} updated (of ${r.data.total} rows)`);
            load();
        } catch { toast.error("Upload failed — check the file format"); }
        setUploading(false);
        e.target.value = "";
    };

    return (
        <div data-testid="dashboard-page">
            <div className="flex items-end justify-between mb-6">
                <div>
                    <h1 className="text-3xl hdb-heading font-black text-slate-900">Dashboard</h1>
                    <p className="text-slate-500 mt-1 text-sm">
                        Overview of your invoicing and GST activity
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Link to="/invoices/from-po" className="hdb-btn hdb-btn-ghost" data-testid="dashboard-import-po">
                        <ScanLine size={14} /> Import from PO
                    </Link>
                    <Link to="/invoices/new" className="hdb-btn hdb-btn-primary" data-testid="dashboard-new-invoice">
                        + New Invoice
                    </Link>
                </div>
            </div>

            {/* Quick actions strip */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                <input ref={fileRef} type="file" accept=".csv,.xlsx,.xlsm" onChange={upload} className="hidden" data-testid="dashboard-csv-input" />
                <button onClick={() => fileRef.current?.click()} disabled={uploading}
                        className="hdb-card p-4 text-left hover:border-slate-900 transition-colors flex items-center gap-4"
                        data-testid="dashboard-upload-rates">
                    <div className="w-10 h-10 rounded-md bg-emerald-500 text-white flex items-center justify-center flex-shrink-0">
                        <Upload size={18} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="hdb-heading font-bold text-slate-900">Upload rates</div>
                        <div className="text-xs text-slate-500 mt-0.5">
                            {uploading ? "Uploading…" : "Import CSV / Excel with B2B + B2C prices"}
                        </div>
                    </div>
                </button>
                <a href={`${API}/items/export.xlsx`} download
                   className="hdb-card p-4 text-left hover:border-slate-900 transition-colors flex items-center gap-4"
                   data-testid="dashboard-download-excel">
                    <div className="w-10 h-10 rounded-md bg-slate-900 text-white flex items-center justify-center flex-shrink-0">
                        <FileSpreadsheet size={18} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="hdb-heading font-bold text-slate-900">Download rates (Excel)</div>
                        <div className="text-xs text-slate-500 mt-0.5">Edit in Excel, save, and re-upload to update</div>
                    </div>
                </a>
                <a href={`${API}/items/export.csv`} download
                   className="hdb-card p-4 text-left hover:border-slate-900 transition-colors flex items-center gap-4"
                   data-testid="dashboard-download-csv">
                    <div className="w-10 h-10 rounded-md bg-sky-600 text-white flex items-center justify-center flex-shrink-0">
                        <Download size={18} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="hdb-heading font-bold text-slate-900">Download rates (CSV)</div>
                        <div className="text-xs text-slate-500 mt-0.5">Same file, but as CSV for Google Sheets</div>
                    </div>
                </a>
                <Link to="/items" className="hdb-card p-4 text-left hover:border-slate-900 transition-colors flex items-center gap-4"
                      data-testid="dashboard-manage-items">
                    <div className="w-10 h-10 rounded-md bg-amber-500 text-white flex items-center justify-center flex-shrink-0">
                        <Plus size={18} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <div className="hdb-heading font-bold text-slate-900">Manage manually</div>
                        <div className="text-xs text-slate-500 mt-0.5">Add or edit rates one by one in Item Master</div>
                    </div>
                </Link>
            </div>

            <div className="stat-grid mb-8">
                {cards.map((c) => (
                    <div key={c.label} className="hdb-card p-5" data-testid={`stat-${c.label}`}>
                        <div className="flex items-start justify-between">
                            <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
                                {c.label}
                            </div>
                            <c.icon size={16} className="text-slate-400" />
                        </div>
                        <div className="mt-3 text-3xl hdb-heading font-black text-slate-900 hdb-num">
                            {c.value}
                        </div>
                    </div>
                ))}
            </div>

            {/* Fresh client orders flash — polls every 20s (P6+) */}
            <NewClientOrdersFlash />

            {/* Pending self-registrations (P8) */}
            <PendingRegistrationsWidget />

            {/* Portal reminders due — manual nudge queue (P6) */}
            <PortalRemindersWidget />

            {/* Top clients (month) with sparklines */}
            <div className="hdb-card p-5 mb-8" data-testid="top-clients-widget">
                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <Crown size={18} className="text-amber-500" />
                        <h3 className="hdb-heading font-bold text-slate-900 text-lg">Top clients this month</h3>
                    </div>
                    <Link to="/clients" className="text-xs text-slate-500 hover:text-slate-900 flex items-center gap-1"
                          data-testid="view-all-clients">
                        View all clients <ArrowRight size={12} />
                    </Link>
                </div>
                {s.top_clients_month.length === 0 ? (
                    <div className="text-sm text-slate-400 py-6 text-center">
                        No client activity yet this month — create an invoice to populate the leaderboard.
                    </div>
                ) : (
                    <table className="w-full">
                        <thead>
                            <tr className="text-[10px] uppercase tracking-widest text-slate-400 border-b border-slate-100">
                                <th className="text-left py-2 font-semibold" style={{ width: 28 }}>#</th>
                                <th className="text-left py-2 font-semibold">Client</th>
                                <th className="text-right py-2 font-semibold">This month</th>
                                <th className="text-right py-2 font-semibold">Lifetime</th>
                                <th className="text-center py-2 font-semibold">6-month trend</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {s.top_clients_month.map((c, i) => {
                                const trending = c.spark.length >= 2 && c.spark[c.spark.length - 1] < c.spark[c.spark.length - 2];
                                return (
                                    <tr key={c.buyer.name + i} className="border-b border-slate-50 last:border-0 hover:bg-slate-50"
                                        data-testid={`top-client-row-${i}`}>
                                        <td className="py-3 hdb-mono text-slate-400 font-semibold">{i + 1}</td>
                                        <td className="py-3">
                                            <div className="flex items-center gap-2">
                                                <div className="font-semibold text-slate-900">{c.buyer.name}</div>
                                                <span className={`hdb-tag ${c.last_invoice_type === "B2C" ? "hdb-tag-gold" : "hdb-tag-mint"}`}>
                                                    {c.last_invoice_type}
                                                </span>
                                            </div>
                                            <div className="text-[11px] text-slate-500 mt-0.5">
                                                {c.invoice_count} invoice{c.invoice_count !== 1 ? "s" : ""} · last on {c.last_order_date || "—"}
                                            </div>
                                        </td>
                                        <td className="hdb-num hdb-num-right font-bold text-slate-900">{fmt(c.month_revenue)}</td>
                                        <td className="hdb-num hdb-num-right text-slate-600">{fmt(c.lifetime_revenue)}</td>
                                        <td className="py-3">
                                            <div className="flex items-center justify-center gap-1">
                                                <Sparkline data={c.spark} color={trending ? "#DC2626" : "#10B981"} />
                                                {trending && (
                                                    <span className="text-[10px] text-red-600 font-semibold ml-1"
                                                          title="Revenue trending down">↓</span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="text-right">
                                            <button className="hdb-btn hdb-btn-ghost text-xs" onClick={() => reorder(c)}
                                                    data-testid={`reorder-${c.buyer.name}`}>
                                                Reorder
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div className="hdb-card p-5">
                    <h3 className="hdb-heading font-bold text-slate-900 mb-3 text-lg">This month</h3>
                    <div className="flex justify-between py-2 border-b border-slate-100 text-sm">
                        <span className="text-slate-500">Revenue</span>
                        <span className="hdb-num font-semibold">{fmt(s.month_revenue)}</span>
                    </div>
                    <div className="flex justify-between py-2 text-sm">
                        <span className="text-slate-500">GST collected</span>
                        <span className="hdb-num font-semibold">{fmt(s.month_tax)}</span>
                    </div>
                </div>

                <div className="hdb-card p-5">
                    <h3 className="hdb-heading font-bold text-slate-900 mb-3 text-lg">Recent invoices</h3>
                    {s.recent.length === 0 && (
                        <div className="text-sm text-slate-500 py-4">No invoices yet — create your first one.</div>
                    )}
                    {s.recent.map((r) => (
                        <Link
                            key={r.id}
                            to={`/invoices/${r.id}`}
                            className="flex justify-between py-2 border-b border-slate-100 last:border-0 text-sm hover:bg-slate-50 px-1"
                            data-testid={`recent-invoice-${r.invoice_number}`}
                        >
                            <div>
                                <div className="hdb-num text-slate-900 font-semibold">{r.invoice_number}</div>
                                <div className="text-xs text-slate-500">{r.buyer.name} · {r.invoice_date}</div>
                            </div>
                            <div className="hdb-num font-semibold">{fmt(r.grand_total)}</div>
                        </Link>
                    ))}
                </div>
            </div>
        </div>
    );
}


/* ------------------------------------------------------------
 * Portal Reminders — clients whose reorder cycle is due today.
 * Reminders are NOT sent automatically — this widget just shows
 * who needs a nudge so HDB staff can reach out via phone/WhatsApp.
 * ------------------------------------------------------------ */
function PortalRemindersWidget() {
    const [list, setList] = useState(null);
    const load = () => api.get("/admin/portal/reminders-due")
        .then((r) => setList(r.data))
        .catch(() => setList([]));
    useEffect(() => { load(); }, []);

    if (list === null) return null;
    if (list.length === 0) {
        return (
            <div className="hdb-card p-5 mb-8 bg-emerald-50/40 border-emerald-200" data-testid="reminders-widget-empty">
                <div className="flex items-center gap-3">
                    <BellRing size={18} className="text-emerald-700" />
                    <div>
                        <h3 className="hdb-heading font-bold text-slate-900">No reorder reminders due</h3>
                        <p className="text-xs text-slate-500 mt-0.5">All portal clients are within their reorder window</p>
                    </div>
                </div>
            </div>
        );
    }

    const markReminded = async (id) => {
        try {
            await api.post(`/admin/portal/reminders-due/${id}/mark-reminded`);
            toast.success("Marked as reminded");
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };

    const copyWhatsApp = async (r) => {
        const items = (r.last_order_items || [])
            .slice(0, 10)
            .map((li) => `• ${li.name}${li.qty ? ` (${li.qty} ${li.unit || ""})` : ""}`)
            .join("\n");
        const msg = `Hi ${r.name}, hope you're doing well! It's been a while since your last order with HDB${r.last_order_number ? ` (${r.last_order_number})` : ""}. Would you like to reorder? Here's what you last got:\n\n${items}\n\nReply with any changes and we'll set it up.`;
        try {
            await navigator.clipboard.writeText(msg);
            toast.success("WhatsApp message copied — paste into your chat");
        } catch { toast.error("Copy failed"); }
    };

    return (
        <div className="hdb-card p-5 mb-8 border-amber-200 bg-amber-50/40" data-testid="reminders-widget">
            <div className="flex items-center justify-between mb-3 gap-3 flex-wrap">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-amber-500 text-white grid place-items-center">
                        <BellRing size={18} />
                    </div>
                    <div>
                        <h3 className="hdb-heading font-bold text-slate-900">Reorder reminders due · {list.length}</h3>
                        <p className="text-xs text-slate-500 mt-0.5">
                            Portal clients ready to be nudged — copy a WhatsApp-ready message or mark done once you've reached out
                        </p>
                    </div>
                </div>
            </div>
            <div className="space-y-2">
                {list.map((r) => (
                    <div key={r.client_id}
                         className="p-3 rounded-xl bg-white border border-slate-200 flex items-center gap-3 flex-wrap"
                         data-testid={`reminder-row-${r.client_id}`}>
                        <div className="flex-1 min-w-0">
                            <div className="font-bold text-slate-900">{r.name}</div>
                            <div className="text-xs text-slate-500">
                                {r.contact_phone && <>📱 {r.contact_phone} · </>}
                                {r.contact_email && <>✉ {r.contact_email} · </>}
                                Cycle: {r.reminder_cycle_days}d ·
                                {r.last_reminded_at
                                    ? ` Last nudged: ${r.last_reminded_at.slice(0,10)}`
                                    : " Never reminded"}
                                {r.last_order_at && <> · Last order: {r.last_order_at.slice(0,10)}</>}
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <button onClick={() => copyWhatsApp(r)}
                                    className="hdb-btn hdb-btn-ghost text-xs"
                                    data-testid={`reminder-copy-${r.client_id}`}>
                                <MessageCircle size={12}/> Copy message
                            </button>
                            <button onClick={() => markReminded(r.client_id)}
                                    className="hdb-btn hdb-btn-mint text-xs"
                                    data-testid={`reminder-done-${r.client_id}`}>
                                <Check size={12}/> Mark reminded
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}


/* ------------------------------------------------------------
 * NewClientOrdersFlash — high-visibility banner that lights up
 * when new client-portal orders arrive. Polls every 20s so staff
 * see submissions without needing to reload the whole app.
 * ------------------------------------------------------------ */
function NewClientOrdersFlash() {
    const [count, setCount] = useState(0);
    const [latest, setLatest] = useState({ latest_at: "", latest_client: "" });
    const [pulse, setPulse] = useState(false);

    const load = async () => {
        try {
            const r = await api.get("/admin/client-orders/count");
            setCount((prev) => {
                if (r.data.pending > prev && prev > 0) setPulse(true);
                return r.data.pending;
            });
            setLatest({ latest_at: r.data.latest_at, latest_client: r.data.latest_client });
        } catch { /* ignore */ }
    };

    useEffect(() => {
        load();
        const t = setInterval(load, 20000);   // 20s
        return () => clearInterval(t);
    }, []);

    // Auto-clear the pulse after animation runs
    useEffect(() => {
        if (!pulse) return;
        const t = setTimeout(() => setPulse(false), 2000);
        return () => clearTimeout(t);
    }, [pulse]);

    if (count === 0) return null;

    return (
        <div className={`hdb-card p-5 mb-6 border-emerald-400 bg-gradient-to-r from-emerald-50 to-emerald-100/60 shadow-lg
                        ${pulse ? "animate-pulse ring-4 ring-emerald-300" : ""}`}
             data-testid="new-client-orders-flash">
            <div className="flex items-center gap-4 flex-wrap">
                <div className="w-12 h-12 rounded-2xl bg-emerald-600 text-white grid place-items-center shadow-md">
                    <span className="text-2xl">🔔</span>
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="hdb-heading font-black text-slate-900 text-lg leading-tight">
                        {count} new client order{count !== 1 ? "s" : ""} waiting
                    </h3>
                    <p className="text-xs text-slate-600 mt-0.5">
                        {latest.latest_client && (
                            <>Latest from <b>{latest.latest_client}</b> at {latest.latest_at?.slice(11, 16)} · </>
                        )}
                        Convert into invoices from the Client Orders inbox.
                    </p>
                </div>
                <a href="/client-orders" className="hdb-btn hdb-btn-primary" data-testid="new-orders-open-inbox">
                    Open inbox <ArrowRight size={13}/>
                </a>
            </div>
        </div>
    );
}

/* ------------------------------------------------------------
 * PendingRegistrationsWidget — admin queue for self-signups
 * that didn't auto-approve (missing / invalid GSTIN).
 * ------------------------------------------------------------ */
function PendingRegistrationsWidget() {
    const [list, setList] = useState(null);

    const load = () => api.get("/admin/portal/pending-registrations")
        .then((r) => setList(r.data))
        .catch(() => setList([]));

    useEffect(() => {
        load();
        const t = setInterval(load, 60000);   // 60s poll
        return () => clearInterval(t);
    }, []);

    if (!list || list.length === 0) return null;

    const approve = async (id) => {
        try {
            const r = await api.post(`/admin/portal/registrations/${id}/approve`);
            // Copy the magic link to clipboard for staff convenience
            try { await navigator.clipboard.writeText(r.data.portal_link); } catch {}
            toast.success("Approved · magic link copied to clipboard");
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };
    const reject = async (id) => {
        if (!window.confirm("Reject this registration?")) return;
        try {
            await api.post(`/admin/portal/registrations/${id}/reject`);
            toast.success("Rejected");
            load();
        } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    };

    return (
        <div className="hdb-card p-5 mb-6 border-blue-300 bg-blue-50/50" data-testid="pending-registrations">
            <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-xl bg-blue-500 text-white grid place-items-center">
                    <span className="text-lg">👋</span>
                </div>
                <div className="flex-1">
                    <h3 className="hdb-heading font-bold text-slate-900">
                        {list.length} new portal registration{list.length !== 1 ? "s" : ""} awaiting approval
                    </h3>
                    <p className="text-xs text-slate-500 mt-0.5">
                        These businesses signed themselves up but didn't have a valid GSTIN — review each below.
                    </p>
                </div>
            </div>
            <div className="space-y-2">
                {list.map((r) => (
                    <div key={r.id}
                         className="p-3 rounded-xl bg-white border border-slate-200 flex items-start gap-3 flex-wrap"
                         data-testid={`pending-reg-${r.id}`}>
                        <div className="flex-1 min-w-0">
                            <div className="font-bold text-slate-900">{r.name}</div>
                            <div className="text-xs text-slate-500 mt-0.5 space-x-2">
                                {r.gstin && <><span className="font-mono">{r.gstin}</span> ·</>}
                                {r.contact_email && <>✉ {r.contact_email}</>}
                                {r.contact_phone && <> · 📱 {r.contact_phone}</>}
                                {r.address && <div className="text-slate-500 mt-1">{r.address}</div>}
                            </div>
                        </div>
                        <div className="flex gap-2">
                            <button onClick={() => approve(r.id)} className="hdb-btn hdb-btn-primary text-xs"
                                    data-testid={`approve-reg-${r.id}`}>
                                Approve · Get link
                            </button>
                            <button onClick={() => reject(r.id)} className="hdb-btn hdb-btn-ghost text-xs text-red-700"
                                    data-testid={`reject-reg-${r.id}`}>
                                Reject
                            </button>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

